// Item 24 — Data Table column instances.
//
// A Data Table column is an INSTANCE, never just a field name: the same field may be assigned
// many times, each occurrence with its own aggregation, "show value as", number format, label
// and width. The model lives on the widget config:
//
//   config.dimensions[i].cid / config.measures[i].cid  -> stable instance id
//   config.table_columns = [{ cid, role: "dim"|"meas" }] -> the rendered column ORDER
//
// The FIRST instance of a field keeps its natural query key (`sum_amount`, `region`) so existing
// dashboards, conditional-formatting rules, per-column styles and saved filters keep working.
// Every DUPLICATE instance gets `alias: "c_<cid>"` + `clone_of: <base key>`:
//   * ad-hoc measures are queried under that alias (one real column per instance),
//   * governed measures / dimensions (where the engine de-duplicates by key) are cloned from the
//     base column client-side by `expandTableInstances`.
// Legacy widgets (no `table_columns`) fall back to "dimensions then measures", unchanged.

export const newCid = () => `c${Math.random().toString(36).slice(2, 8)}`;

export function dimBaseKey(d) {
  if (!d) return null;
  if (d.key) return d.key;
  if (d.band || d.bin) return d.alias || null;
  return d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field || null;
}

export function measBaseKey(m) {
  if (!m) return null;
  if (m.key) return m.key;
  return m.field ? `${m.agg || "sum"}_${m.field}` : null;
}

export const baseKeyOf = (entry, role) => (role === "dim" ? dimBaseKey(entry) : measBaseKey(entry));
export const instanceKeyOf = (entry, role) => (entry && entry.alias) || baseKeyOf(entry, role);

// Ordered column instances: [{ cid, role, index, entry }]
export function tableInstances(config = {}) {
  const dims = (config.dimensions || []).map((entry, index) => ({ role: "dim", index, cid: entry?.cid || `d${index}`, entry: entry || {} }));
  const meas = (config.measures || []).map((entry, index) => ({ role: "meas", index, cid: entry?.cid || `m${index}`, entry: entry || {} }));
  const all = [...dims, ...meas];
  const order = Array.isArray(config.table_columns) ? config.table_columns : null;
  if (!order || !order.length) return all;
  const byCid = new Map(all.map((x) => [x.cid, x]));
  const out = [];
  order.forEach((o) => {
    const x = o && byCid.get(o.cid);
    if (x) { out.push(x); byCid.delete(o.cid); }
  });
  byCid.forEach((x) => out.push(x));  // fields added outside the table editor land at the end
  return out;
}

// Instances -> config patch. Assigns cids, duplicate aliases and the column order.
export function instancesToConfig(instances) {
  const dimensions = [];
  const measures = [];
  const table_columns = [];
  const seen = new Set();
  (instances || []).forEach((inst) => {
    if (!inst) return;
    const role = inst.role === "dim" ? "dim" : "meas";
    const cid = inst.cid || newCid();
    const entry = { ...(inst.entry || {}), cid };
    const bk = baseKeyOf({ ...entry, alias: entry.clone_of ? undefined : entry.alias }, role) || baseKeyOf(entry, role);
    // Item 35: the same governed date field at two hierarchy levels is NOT a duplicate —
    // each level is queried as its own column instead of being cloned client-side.
    const part = role === "dim" && entry.date_part && entry.date_part !== "none" ? entry.date_part : null;
    const key = entry.clone_of || (part && bk && !String(bk).endsWith(`_${part}`) ? `${bk}_${part}` : bk);
    if (key && seen.has(key)) {
      entry.alias = `c_${cid}`;
      entry.clone_of = key;
    } else {
      if (entry.clone_of) { delete entry.clone_of; delete entry.alias; }
      if (part && key !== bk && seen.has(bk)) entry.alias = key;  // second level on the same field
      else if (entry.alias && entry.alias === `${bk}_${part}`) delete entry.alias;
      if (key) seen.add(key);
      if (bk) seen.add(bk);
    }
    (role === "dim" ? dimensions : measures).push(entry);
    table_columns.push({ cid, role });
  });
  return { dimensions, measures, table_columns };
}

export function moveInstance(list, from, to) {
  const arr = [...(list || [])];
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const [it] = arr.splice(from, 1);
  arr.splice(to, 0, it);
  return arr;
}

// Duplicate instances that the query engine collapsed (governed measures, dimensions) are
// materialised here so every column instance has its own data column + meta entry.
export function expandTableInstances(data, config = {}) {
  if (!data || !Array.isArray(data.columns)) return data;
  const cols = new Set(data.columns);
  const clones = [];
  const scan = (arr, role) => (arr || []).forEach((e) => {
    if (!e || !e.alias || !e.clone_of) return;
    if (cols.has(e.alias) || !cols.has(e.clone_of)) return;
    clones.push({ key: e.alias, base: e.clone_of, role, entry: e });
    cols.add(e.alias);
  });
  scan(config.dimensions, "dim");
  scan(config.measures, "meas");
  if (!clones.length) return data;
  const rows = (data.rows || []).map((r) => {
    const o = { ...r };
    clones.forEach((c) => { o[c.key] = r[c.base]; });
    return o;
  });
  const meta = { ...(data.meta || {}) };
  const meas = [...(meta.measures || [])];
  const dims = [...(meta.dimensions || [])];
  clones.forEach((c) => {
    const inMeas = meas.find((x) => x.key === c.base);
    const src = inMeas || dims.find((x) => x.key === c.base) || {};
    const cloned = { ...src, key: c.key, ...(c.entry.label ? { label: c.entry.label } : {}) };
    if (inMeas || c.role === "meas") meas.push(cloned); else dims.push(cloned);
  });
  meta.measures = meas;
  meta.dimensions = dims;
  return { ...data, columns: [...data.columns, ...clones.map((c) => c.key)], rows, meta };
}

// The rendered column keys in the author's order. Returns null for legacy widgets so callers
// keep using the raw query order.
export function tableColumnOrder(config = {}, dataColumns = []) {
  if (!Array.isArray(config.table_columns) || !config.table_columns.length) return null;
  const avail = new Set(dataColumns || []);
  const seen = new Set();
  const out = [];
  tableInstances(config).forEach((i) => {
    const k = instanceKeyOf(i.entry, i.role);
    if (!k || !avail.has(k) || seen.has(k)) return;
    seen.add(k);
    out.push(k);
  });
  (dataColumns || []).forEach((k) => { if (!seen.has(k)) { seen.add(k); out.push(k); } });
  return out;
}
